import axios from "axios";
import { openPaywall } from "../paywall";
import { getDeviceIdSync } from "../device";

// iter-230 — Resolve the backend base URL defensively. Previously
// `${REACT_APP_BACKEND_URL}/api` would silently become
// `undefined/api` or `http://localhost:8001/api` when the production
// build ran without the right env file — the browser then fired
// requests at a dead host and surfaced ERR_CONNECTION_REFUSED (what
// the user hit on their VPS). We now:
//   1. Trim trailing slashes.
//   2. Treat any obviously-not-a-public-URL value (undefined, empty,
//      the string "undefined", localhost, 127.*, 0.0.0.0) as "use the
//      page origin instead".
//   3. In the browser, also fall back to "" when the env host is
//      different from `window.location.hostname` but is a loopback /
//      private-IP string — those can't resolve from a visitor's device.
// Setting `baseURL=""` makes axios issue same-origin relative URLs
// (`/api/auth/login` → `https://planflow.pro/api/auth/login`), which
// is proxied to the backend by the frontend container.
function _resolveBackendBase() {
    const raw = process.env.REACT_APP_BACKEND_URL;
    const v = (raw == null ? "" : String(raw)).trim().replace(/\/+$/, "");
    if (!v || v.toLowerCase() === "undefined" || v.toLowerCase() === "null") {
        return "";
    }
    try {
        // Catch dev-only hosts that would never work in a browser the
        // user actually opens on their phone / another machine.
        const u = new URL(v);
        const h = (u.hostname || "").toLowerCase();
        const isLoopback =
            h === "localhost" ||
            h === "0.0.0.0" ||
            h.startsWith("127.") ||
            h.endsWith(".local");
        if (isLoopback && typeof window !== "undefined") {
            const sameAsPage = h === (window.location.hostname || "").toLowerCase();
            if (!sameAsPage) return "";
        }
    } catch {
        // not a valid URL — safest to fall back to same-origin
        return "";
    }
    return v;
}

const BACKEND_URL = _resolveBackendBase();
export const API = `${BACKEND_URL}/api`;

/**
 * iter-230 — Safe absolute URL builder for static assets served by
 * the backend (logos, hero videos, invoice files). Pass a server-
 * relative path (e.g. "/api/static/branding/logo.png") and get back
 * the correct absolute URL, OR the same relative path when the
 * frontend is running same-origin as the backend. Never produces
 * "undefined/..." or a loopback URL that would break for real
 * visitors.
 */
export function assetUrl(path) {
    if (!path) return "";
    const s = String(path);
    if (/^https?:\/\//i.test(s)) return s;        // already absolute
    const base = BACKEND_URL;                      // "" when same-origin
    const normalised = s.startsWith("/") ? s : `/${s}`;
    return `${base}${normalised}`;
}

export const api = axios.create({
    baseURL: API,
});

// iter-97: attach a stable per-device id to every request so the
// backend can flag duplicate-account attempts. Safe on web (random
// per-browser id) and on native (Capacitor Device.getId()).
api.interceptors.request.use((config) => {
    try {
        const did = getDeviceIdSync();
        if (did) {
            config.headers = config.headers || {};
            config.headers["X-Device-Id"] = did;
        }
    } catch {
        // no-op — header is purely best-effort
    }
    return config;
});

// Global 402 → paywall interceptor. Backend `ensure_credits()` raises
// 402 with a structured `detail` payload ({code, message, balance,
// needed_credits, provider}); we surface that to the global modal so
// every AI feature gets the same paywall treatment without per-call
// catch blocks.
api.interceptors.response.use(
    (resp) => resp,
    (err) => {
        if (err?.response?.status === 402) {
            const detail = err.response.data?.detail || {};
            openPaywall(typeof detail === "object" ? detail : { message: String(detail) });
        }
        return Promise.reject(err);
    },
);
